import React from 'react';
import moment from 'moment';
import type { LucideIcon } from 'lucide-react';
import Tooltip from '../tooltip/Tooltip';

export type Instant = {
    /** Completes `instant-…` as the chip's test id, and keys it in the list. */
    key: string;
    label: string;
    icon: LucideIcon;
    /** ISO 8601. Rendered in UTC. */
    at: string;
};

type InstantChipsProps = {
    instants: Instant[];
    testId: string;
};

/** UTC, and said so: a reader in another timezone must not be shown a shifted instant. */
const formatInstant = (at: string) => moment.utc(at).format('D MMM YYYY HH:mm [UTC]');

/**
 * A row of instants, one icon each, with the date a hover away.
 *
 * Records tend to carry several timestamps that mean different things — when a thing happened,
 * when we learnt of it, when it was last corrected. Spelling all of them out crowds whatever
 * they sit beside, so each becomes an icon that gives its date back on demand.
 *
 * Which instants exist is the caller's business: it passes the ones its record actually carries,
 * so an absent one is left out rather than rendered empty.
 */
const InstantChips = ({ instants, testId }: InstantChipsProps) => (
    <span className='inline-flex items-center gap-1' data-testid={testId}>
        {instants.map(({ key, label, icon: Icon, at }) => (
            <Tooltip key={key} content={`${label} ${formatInstant(at)}`} placement='top'>
                <span
                    aria-label={`${label} ${formatInstant(at)}`}
                    data-testid={`instant-${key}`}
                    className='inline-flex cursor-help items-center rounded bg-secondary p-1 text-muted-foreground'
                >
                    <Icon className='h-3.5 w-3.5' aria-hidden='true' />
                </span>
            </Tooltip>
        ))}
    </span>
);

export default InstantChips;
